/*
 * Copyright 2022, TeamDev. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Redistribution and use in source and/or binary forms, with or without
 * modification, must retain the above copyright notice and the following
 * disclaimer.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR
 * A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT
 * OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
 * SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT
 * LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY
 * THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
 * OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.CustomClientFactory = exports.AbstractClientFactory = void 0;

var _actorRequestFactory = require("./actor-request-factory");

var _client = require("./client");

var _commandingClient = require("./commanding-client");

var _httpClient = require("./http-client");

var _httpEndpoint = require("./http-endpoint");

function _get() { if (typeof Reflect !== "undefined" && Reflect.get) { _get = Reflect.get.bind(); } else { _get = function _get(target, property, receiver) { var base = _superPropBase(target, property); if (!base) return; var desc = Object.getOwnPropertyDescriptor(base, property); if (desc.get) { return desc.get.call(arguments.length < 3 ? target : receiver); } return desc.value; }; } return _get.apply(this, arguments); }

function _superPropBase(object, property) { while (!Object.prototype.hasOwnProperty.call(object, property)) { object = _getPrototypeOf(object); if (object === null) break; } return object; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf(subClass, superClass); }

function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized(self); }

function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }

function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }

/**
 * @typedef {Object} ClientOptions a type of object for initialization of Spine client
 *
 * @property {!Array<Object>} protoIndexFiles
 *  the list of the `index.js` files generated by {@link https://github.com/SpineEventEngine/base/tree/master/tools/proto-js-plugin the Protobuf plugin for JS}
 * @property {?string} endpointUrl
 *  the URL of the Spine-based backend endpoint
 * @property {?firebase.database.Database} firebaseDatabase
 *  the Firebase Database that will be used to retrieve data from
 * @property {?ActorProvider} actorProvider
 *  the provider of the user interacting with Spine
 * @property {?Client} implementation
 *  the custom implementation of `Client`
 * @property {?Routing} routing
 *  custom configuration of HTTP endpoints
 * @property {?TenantProvider} tenantProvider
 *  the provider of an active tenant ID, if not specified, the application is considered
 *  single-tenant
 * @property {?Duration} subscriptionKeepUpInterval
 *  the custom interval for sending requests to keep up subscriptions
 */

/**
 * An abstract factory for creation of `Client` instances.
 *
 * Ensures that the `ClientOptions` contain list of the `index.js` files generated by
 * {@link https://github.com/SpineEventEngine/base/tree/master/tools/proto-js-plugin the Protobuf plugin for JS}
 * and performs registration of types and parsers containing in these files.
 *
 * Creation of the concrete implementation of `Client` instances is delegated to inheritors.
 */
var AbstractClientFactory = /*#__PURE__*/function () {
  function AbstractClientFactory() {
    _classCallCheck(this, AbstractClientFactory);
  }

  _createClass(AbstractClientFactory, null, [{
    key: "createClient",
    value:
    /**
     * Creates a new instance of `Client` implementation in accordance with given options.
     *
     * @param {!ClientOptions} options client initialization options
     * @return {Client} a `Client` instance
     */
    function createClient(options) {
      this._ensureOptionsSufficient(options);

      return this._clientFor(options);
    }
    /**
     * Creates a new instance of `Client` implementation in accordance with given options.
     *
     * @param {!ClientOptions} options
     * @return {Client}
     * @protected
     */

  }, {
    key: "_clientFor",
    value: function _clientFor(options) {
      throw new Error('Not implemented in abstract base');
    }
    /**
     * Creates a new instance of `QueryingClient` implementation in accordance with given options.
     *
     * @param {!ClientOptions} options client initialization options
     * @return {QueryingClient} a `QueryingClient` instance
     */

  }, {
    key: "createQuerying",
    value: function createQuerying(options) {
      throw new Error('Not implemented in abstract base');
    }
    /**
     * Creates a new instance of `SubscribingClient` implementation in accordance with given options.
     *
     * @param {!ClientOptions} options client initialization options
     * @return {SubscribingClient} a `SubscribingClient` instance
     */

  }, {
    key: "createSubscribing",
    value: function createSubscribing(options) {
      throw new Error('Not implemented in abstract base');
    }
    /**
     * Creates a new instance of `CommandingClient` implementation in accordance with given options.
     *
     * @param {!ClientOptions} options client initialization options
     * @return {CommandingClient} a `CommandingClient` instance
     */

  }, {
    key: "createCommanding",
    value: function createCommanding(options) {
      var httpClient = new _httpClient.HttpClient(options.endpointUrl);
      var endpoint = new _httpEndpoint.HttpEndpoint(httpClient, options.routing);

      var requestFactory = _actorRequestFactory.ActorRequestFactory.create(options);

      return new _commandingClient.CommandingClient(endpoint, requestFactory);
    }
    /**
     * Ensures whether options object is sufficient for client initialization.
     *
     * @param {!ClientOptions} options
     * @protected
     */

  }, {
    key: "_ensureOptionsSufficient",
    value: function _ensureOptionsSufficient(options) {
      if (!options) {
        throw new Error('Unable to initialize client. The `ClientOptions` is undefined.');
      }

      var indexFiles = options.protoIndexFiles;

      if (!Array.isArray(indexFiles) || indexFiles.length === 0) {
        throw new Error('Only a non-empty array is allowed as ClientOptions.protoIndexFiles parameter.');
      }

      indexFiles.forEach(function (indexFile) {
        if (_typeof(indexFile) !== 'object' || !(indexFile.types instanceof Map) || !(indexFile.parsers instanceof Map)) {
          throw new Error('Unable to register Protobuf index files.' + ' Check the `ClientOptions.protoIndexFiles` contains files' + ' generated with "io.spine.tools:spine-proto-js-plugin".');
        }
      });
    }
  }]);

  return AbstractClientFactory;
}();
/**
 * An implementation of the `AbstractClientFactory` that returns a client instance
 * provided in `ClientOptions` parameter.
 */


exports.AbstractClientFactory = AbstractClientFactory;

var CustomClientFactory = /*#__PURE__*/function (_AbstractClientFactor) {
  _inherits(CustomClientFactory, _AbstractClientFactor);

  var _super = _createSuper(CustomClientFactory);

  function CustomClientFactory() {
    _classCallCheck(this, CustomClientFactory);

    return _super.apply(this, arguments);
  }

  _createClass(CustomClientFactory, null, [{
    key: "_clientFor",
    value:
    /**
     * Returns a custom `Client` implementation provided in options. Expects that the given options
     * contain an implementation which extends `Client`.
     *
     * Can be used to provide mock implementations of `Client`.
     *
     * @param {ClientOptions} options
     * @return {Client} a custom `Client` implementation provided in options
     * @override
     */
    function _clientFor(options) {
      return options.implementation;
    }
    /**
     * Returns a custom `QueryingClient` implementation provided in options. Expects that the given
     * options contain an implementation which extends `QueryingClient`.
     *
     * Can be used to provide mock implementations of `QueryingClient`.
     *
     * @param {ClientOptions} options
     * @return {QueryingClient} a custom `QueryingClient` implementation provided in options
     * @override
     */

  }, {
    key: "createQuerying",
    value: function createQuerying(options) {
      return options.implementation;
    }
    /**
     * Returns a custom `SubscribingClient` implementation provided in options. Expects that the given
     * options contain an implementation which extends `SubscribingClient`.
     *
     * Can be used to provide mock implementations of `SubscribingClient`.
     *
     * @param {ClientOptions} options
     * @return {SubscribingClient} a custom `SubscribingClient` implementation provided in options
     * @override
     */

  }, {
    key: "createSubscribing",
    value: function createSubscribing(options) {
      return options.implementation;
    }
    /**
     * Returns a custom `CommandingClient` implementation provided in options. Expects that the given
     * options contain an implementation which extends `CommandingClient`.
     *
     * Can be used to provide mock implementations of `CommandingClient`.
     *
     * @param {ClientOptions} options
     * @return {CommandingClient} a custom `CommandingClient` implementation provided in options
     * @override
     */

  }, {
    key: "createCommanding",
    value: function createCommanding(options) {
      return options.implementation;
    }
    /**
     * @override
     */

  }, {
    key: "_ensureOptionsSufficient",
    value: function _ensureOptionsSufficient(options) {
      _get(_getPrototypeOf(CustomClientFactory), "_ensureOptionsSufficient", this).call(this, options);

      var customClient = options.implementation;

      if (!customClient || !(customClient instanceof _client.Client)) {
        throw new Error('Unable to initialize custom implementation.' + ' The `ClientOptions.implementation` should extend Client.');
      }
    }
  }]);

  return CustomClientFactory;
}(AbstractClientFactory);

exports.CustomClientFactory = CustomClientFactory;
//# sourceMappingURL=client-factory.js.map